"use client";

import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge, Button, Card, EmptyState, Input, PageHeader, Select, Skeleton, Spinner } from "@/components/ui";
import { FailureInsights } from "@/components/failure-insights";
import { Pagination, statusTone } from "@/components/table";
import { downloadCsv, dateStamp, fetchAllPages } from "@/lib/export";
import type { MessageReport } from "@/lib/types";

const PAGE_SIZE = 25;
const STATUS_OPTIONS = ["", "PENDING", "RETRY", "PROCESSING", "SUBMITTED", "DELIVERED", "FAILED", "EXPIRED", "UNDELIVERABLE", "REJECTED"];

function dayStartIso(d: string): string {
  return d ? new Date(`${d}T00:00:00`).toISOString() : "";
}
function dayEndIso(d: string): string {
  return d ? new Date(`${d}T23:59:59.999`).toISOString() : "";
}
function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export default function DeliveryReportsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<MessageReport[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const filterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim().replace(/\D/g, ""));
    if (status) params.set("status", status);
    if (dateFrom) params.set("dateFrom", dayStartIso(dateFrom));
    if (dateTo) params.set("dateTo", dayEndIso(dateTo));
    return params;
  }, [search, status, dateFrom, dateTo]);

  async function exportCsv() {
    setExporting(true);
    try {
      const all = await fetchAllPages<MessageReport>(async (off, lim) => {
        const params = filterParams();
        params.set("limit", String(lim));
        params.set("offset", String(off));
        const r = await api<{ messages: MessageReport[]; hasMore: boolean }>(`sms/messages?${params.toString()}`);
        return { items: r.messages || [], hasMore: Boolean(r.hasMore) };
      });
      downloadCsv(
        `delivery-report-${dateStamp()}`,
        ["To", "From", "Status", "Sent", "Submitted", "Delivered", "Error"],
        all.map((m) => [m.destination, m.sourceAddr || "", m.status, m.createdAt || "", m.submittedAt || "", m.deliveredAt || "", m.lastError || ""])
      );
    } finally {
      setExporting(false);
    }
  }

  const load = useCallback(
    (nextOffset = 0) => {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(nextOffset) });
      if (search.trim()) params.set("search", search.trim().replace(/\D/g, ""));
      if (status) params.set("status", status);
      if (dateFrom) params.set("dateFrom", dayStartIso(dateFrom));
      if (dateTo) params.set("dateTo", dayEndIso(dateTo));
      api<{ messages: MessageReport[]; hasMore: boolean }>(`sms/messages?${params.toString()}`)
        .then((r) => {
          setRows(r.messages || []);
          setHasMore(Boolean(r.hasMore));
          setOffset(nextOffset);
        })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    },
    [search, status, dateFrom, dateTo]
  );

  useEffect(() => {
    load(0);
  }, [status, dateFrom, dateTo, load]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery Reports"
        subtitle="Every message you've sent, with its delivery status."
        actions={
          <Button variant="secondary" onClick={exportCsv} disabled={exporting || (rows?.length ?? 0) === 0}>
            {exporting ? <Spinner /> : "Export CSV"}
          </Button>
        }
      />

      {/* Why messages failed — same date window as the table below. */}
      <FailureInsights dateFrom={dateFrom || undefined} dateTo={dateTo || undefined} />

      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(0);
          }}
          className="mb-4 flex flex-wrap items-end gap-3"
        >
          <div className="w-full sm:w-52">
            <Input label="Phone number" value={search} onChange={(e) => setSearch(e.target.value.replace(/\D/g, "").slice(0, 15))} placeholder="2526xxxxxxx" />
          </div>
          <div className="w-full sm:w-40">
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s || "All statuses"}</option>
              ))}
            </Select>
          </div>
          <Input label="From" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <Input label="To" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          <Button type="submit" variant="secondary">Search</Button>
          {(search || status || dateFrom || dateTo) && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearch("");
                setStatus("");
                setDateFrom("");
                setDateTo("");
                load(0);
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {rows === null || (loading && rows.length === 0) ? (
          <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No messages" description="No messages match your filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                {/* On a phone only the columns that matter are shown (who / status / when); the
                    rest appear from `sm` up, so the table reads without sideways scrolling. */}
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">To</th>
                  <th className="hidden py-2 pr-4 font-medium sm:table-cell">From</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Sent</th>
                  <th className="hidden py-2 pr-4 font-medium lg:table-cell">Delivered</th>
                  <th className="hidden py-2 pr-4 font-medium lg:table-cell">Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.smsId} className="border-b border-gray-100">
                    <td className="py-2 pr-4 tabular-nums text-gray-700">
                      {m.destination}
                      {/* The hidden sender still matters on mobile — tuck it under the number. */}
                      {m.sourceAddr && <span className="block text-xs text-gray-400 sm:hidden">from {m.sourceAddr}</span>}
                    </td>
                    <td className="hidden py-2 pr-4 text-gray-600 sm:table-cell">{m.sourceAddr || "—"}</td>
                    <td className="py-2 pr-4">
                      <Badge tone={statusTone(m.status)}>{m.status}</Badge>
                      {m.lastError && <span className="block text-xs text-danger-600 lg:hidden">{m.lastError}</span>}
                    </td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{fmtTime(m.createdAt)}</td>
                    <td className="hidden py-2 pr-4 text-xs text-gray-500 lg:table-cell">{fmtTime(m.deliveredAt)}</td>
                    <td className="hidden py-2 pr-4 text-xs text-danger-600 lg:table-cell">{m.lastError || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows && rows.length > 0 && (
          <Pagination
            offset={offset}
            count={rows.length}
            hasMore={hasMore}
            loading={loading}
            onPrev={() => load(Math.max(offset - PAGE_SIZE, 0))}
            onNext={() => load(offset + PAGE_SIZE)}
          />
        )}
      </Card>
    </div>
  );
}
